import {
  Button,
  ComboBox,
  ComboBoxItem,
  Field,
} from "@cvpartner/design-system";
import { User01 } from "@cvpartner/design-system/icons/line";
import * as React from "react";
import { useImpersonationUsers } from "./queries/impersonationUsers.ts";
import { Form } from "./shared/Form.tsx";
import { CardContent } from "./shared/LoginCard.tsx";
import { LoginButtonWrapper, Wrapper } from "./shared/Modern.tsx";

interface User {
  id: string;
  name: string | null;
  email: string;
}

const ImpersonationForm: React.FC = () => {
  const users = useImpersonationUsers();
  const [selectedUser, setSelectedUser] = React.useState<User>();
  const [inputText, setInputText] = React.useState<string>();

  const filteredUsers = React.useMemo(() => {
    if (!inputText || inputText === (selectedUser?.name ?? selectedUser?.id)) {
      return users;
    }

    const lowerCaseInputValue = inputText.toLowerCase();

    return users.filter((user) => {
      if (user.name?.toLowerCase().includes(lowerCaseInputValue)) {
        return true;
      }

      if (user.email?.toLowerCase().includes(lowerCaseInputValue)) {
        return true;
      }

      if (user.id === lowerCaseInputValue) {
        return true;
      }

      return false;
    });
  }, [inputText, selectedUser, users]);

  if (users.length === 0) {
    return (
      <CardContent>
        No international managers found. Is the subdomain correct?
      </CardContent>
    );
  }

  return (
    <Form action="/session/admin/impersonate">
      <CardContent>
        <Field
          label="Pick a user (international managers only)"
          description="You can filter by name, email or ID"
        >
          <ComboBox
            name="user_id"
            virtualized={users.length > 100}
            items={filteredUsers}
            onChange={(key) => {
              if (key) {
                const user = users.find((user) => user.id === key);
                setSelectedUser(user);
              } else {
                setSelectedUser(undefined);
              }
            }}
            onInputChange={setInputText}
          >
            {(user) => (
              <ComboBoxItem textValue={user.name || user.id} icon={User01}>
                {user.name || user.id}
              </ComboBoxItem>
            )}
          </ComboBox>
        </Field>
        {selectedUser ? (
          <>
            <div>ID: {selectedUser.id}</div>
            <div>Email: {selectedUser.email}</div>
          </>
        ) : null}
        <Button
          hierarchy="primary"
          type="submit"
          disabled={selectedUser == null}
        >
          Sign in as selected user
        </Button>
      </CardContent>
    </Form>
  );
};

export const LoginAdminAs: React.FC = () => (
  <Wrapper heading="Sign in as">
    <React.Suspense
      fallback={
        <LoginButtonWrapper center={true}>
          <Button hierarchy="primary" variant="iconOnly" loading={true} />
        </LoginButtonWrapper>
      }
    >
      <ImpersonationForm />
    </React.Suspense>
  </Wrapper>
);
